import {
	DefinedUserPermissions,
	type UserPermission,
} from '../../../../../server/src/constant/permissions';

export {
	DefinedUserPermissions,
	DefinedUserPermissionsArray,
	type UserPermission,
} from '../../../../../server/src/constant/permissions';

export interface IPermissionGroup {
	label: string;
	permissions: {
		key: UserPermission;
		label: string;
		/** A one-or-two-word form of `label`, for compact display such as a pill. */
		shortLabel: string;
		description: string;
	}[];
}

// The permissions themselves come from the server's own file, the single source of truth. Only the
// grouping, the labels and the descriptions people read belong to the clients. Every permission needs a
// description: it is what explains, in the role screens, what turning it on lets someone do.
export const permissionCatalog: IPermissionGroup[] = [
	{
		label: 'Roles',
		permissions: [
			{
				key: DefinedUserPermissions.role.view,
				label: 'View roles',
				shortLabel: 'View',
				description: 'See the roles of the business and what each one allows.',
			},
			{
				key: DefinedUserPermissions.role.add,
				label: 'Add roles',
				shortLabel: 'Add',
				description: 'Create new roles and choose their permissions.',
			},
			{
				key: DefinedUserPermissions.role.edit,
				label: 'Edit roles',
				shortLabel: 'Edit',
				description:
					'Rename a role or change its permissions. The change applies to everyone who has the role.',
			},
			{
				key: DefinedUserPermissions.role.delete,
				label: 'Delete roles',
				shortLabel: 'Delete',
				description: 'Remove a role that nobody holds any more.',
			},
		],
	},
	{
		label: 'Users',
		permissions: [
			{
				key: DefinedUserPermissions.user.view,
				label: 'View users',
				shortLabel: 'View',
				description:
					'See the team, with each person profile, status and activity.',
			},
			{
				key: DefinedUserPermissions.user.add,
				label: 'Invite users',
				shortLabel: 'Invite',
				description:
					'Add a new team member and get their temporary sign-in details.',
			},
			{
				key: DefinedUserPermissions.user.edit,
				label: 'Edit users',
				shortLabel: 'Edit',
				description:
					'Change a team member details, suspend or restore their access, and deactivate them.',
			},
			{
				key: DefinedUserPermissions.user.delete,
				label: 'Remove users',
				shortLabel: 'Remove',
				description: 'Reset the temporary password of a team member.',
			},
			{
				key: DefinedUserPermissions.user.assignRole,
				label: 'Assign roles to users',
				shortLabel: 'Assign roles',
				description:
					'Give or take away roles. A role carries its permissions, so this decides what each person can do.',
			},
		],
	},
	{
		label: 'Customers',
		permissions: [
			{
				key: DefinedUserPermissions.customer.view,
				label: 'View customers',
				shortLabel: 'View',
				description: 'See the customer list and the details of each customer.',
			},
			{
				key: DefinedUserPermissions.customer.add,
				label: 'Add customers',
				shortLabel: 'Add',
				description: 'Create new customers, individuals or companies.',
			},
			{
				key: DefinedUserPermissions.customer.edit,
				label: 'Edit customers',
				shortLabel: 'Edit',
				description:
					'Change the name, phone number, email or contact person of a customer.',
			},
			{
				key: DefinedUserPermissions.customer.delete,
				label: 'Delete customers',
				shortLabel: 'Delete',
				description:
					'Remove a customer from the list. Past sales keep their details.',
			},
			{
				key: DefinedUserPermissions.customer.import,
				label: 'Import customers',
				shortLabel: 'Import',
				description: 'Add many customers at once from a CSV or XLSX file.',
			},
			{
				key: DefinedUserPermissions.customer.export,
				label: 'Export customers',
				shortLabel: 'Export',
				description:
					'Download the customer list from the app. It holds personal details, so give it carefully.',
			},
		],
	},
	{
		label: 'Surveys',
		permissions: [
			{
				key: DefinedUserPermissions.survey.view,
				label: 'View surveys',
				shortLabel: 'View',
				description:
					'See the surveys built for the business and the responses they collect.',
			},
			{
				key: DefinedUserPermissions.survey.add,
				label: 'Create surveys',
				shortLabel: 'Add',
				description:
					'Build new surveys and open or close them to new responses.',
			},
			{
				key: DefinedUserPermissions.survey.delete,
				label: 'Delete surveys',
				shortLabel: 'Delete',
				description:
					'Remove a survey. Responses already collected are removed with it.',
			},
		],
	},
	{
		label: 'Products',
		permissions: [
			{
				key: DefinedUserPermissions.product.view,
				label: 'View products',
				shortLabel: 'View',
				description: 'See the product list with prices.',
			},
			{
				key: DefinedUserPermissions.product.add,
				label: 'Add products',
				shortLabel: 'Add',
				description: 'Create new products and set their opening stock.',
			},
			{
				key: DefinedUserPermissions.product.edit,
				label: 'Edit products',
				shortLabel: 'Edit',
				description:
					'Change the name, unit, code and other details of a product. Prices have their own permission.',
			},
			{
				key: DefinedUserPermissions.product.editPrice,
				label: 'Change product prices',
				shortLabel: 'Change prices',
				description:
					'Change what a product sells for. Kept apart from editing so a price cannot change by accident.',
			},
			{
				key: DefinedUserPermissions.product.delete,
				label: 'Delete products',
				shortLabel: 'Delete',
				description:
					'Remove a product so it can no longer be sold. Past sales keep its name and price.',
			},
			{
				key: DefinedUserPermissions.product.import,
				label: 'Import products',
				shortLabel: 'Import',
				description: 'Add many products at once from a file.',
			},
			{
				key: DefinedUserPermissions.product.export,
				label: 'Export products',
				shortLabel: 'Export',
				description: 'Download the product list from the app.',
			},
		],
	},
	{
		label: 'Stock',
		permissions: [
			{
				key: DefinedUserPermissions.stock.view,
				label: 'View stock',
				shortLabel: 'View',
				description:
					'See how many of each product are in stock and which ones are running low.',
			},
			{
				key: DefinedUserPermissions.stock.adjust,
				label: 'Adjust stock',
				shortLabel: 'Adjust',
				description:
					'Change a quantity: a recount, damaged goods, a delivery or a correction. This changes real numbers, so give it carefully.',
			},
		],
	},
	{
		label: 'Categories',
		permissions: [
			{
				key: DefinedUserPermissions.productCategory.view,
				label: 'View categories',
				shortLabel: 'View',
				description: 'See the product categories.',
			},
			{
				key: DefinedUserPermissions.productCategory.add,
				label: 'Add categories',
				shortLabel: 'Add',
				description: 'Create new categories.',
			},
			{
				key: DefinedUserPermissions.productCategory.edit,
				label: 'Edit categories',
				shortLabel: 'Edit',
				description: 'Rename or change a category.',
			},
			{
				key: DefinedUserPermissions.productCategory.delete,
				label: 'Delete categories',
				shortLabel: 'Delete',
				description: 'Remove a category.',
			},
		],
	},
	{
		label: 'Value lists',
		permissions: [
			{
				key: DefinedUserPermissions.valueList.view,
				label: 'View value lists',
				shortLabel: 'View',
				description:
					'See the shared lists the app offers as choices, such as units or reasons.',
			},
			{
				key: DefinedUserPermissions.valueList.add,
				label: 'Add value lists',
				shortLabel: 'Add',
				description: 'Create new lists or add items to them.',
			},
			{
				key: DefinedUserPermissions.valueList.edit,
				label: 'Edit value lists',
				shortLabel: 'Edit',
				description: 'Change the items in a list.',
			},
			{
				key: DefinedUserPermissions.valueList.delete,
				label: 'Delete value lists',
				shortLabel: 'Delete',
				description: 'Remove a list or an item from it.',
			},
		],
	},
	{
		label: 'Suppliers',
		permissions: [
			{
				key: DefinedUserPermissions.supplier.view,
				label: 'View suppliers',
				shortLabel: 'View',
				description: 'See the people and companies you buy from.',
			},
			{
				key: DefinedUserPermissions.supplier.add,
				label: 'Add suppliers',
				shortLabel: 'Add',
				description: 'Create new suppliers.',
			},
			{
				key: DefinedUserPermissions.supplier.edit,
				label: 'Edit suppliers',
				shortLabel: 'Edit',
				description: 'Change the details of a supplier.',
			},
			{
				key: DefinedUserPermissions.supplier.delete,
				label: 'Delete suppliers',
				shortLabel: 'Delete',
				description: 'Remove a supplier.',
			},
			{
				key: DefinedUserPermissions.supplier.import,
				label: 'Import suppliers',
				shortLabel: 'Import',
				description: 'Add many suppliers at once from a CSV or XLSX file.',
			},
			{
				key: DefinedUserPermissions.supplier.pay,
				label: 'Pay suppliers',
				shortLabel: 'Pay',
				description: 'Record money paid to a supplier against a bill.',
			},
		],
	},
	{
		label: 'Purchase orders',
		permissions: [
			{
				key: DefinedUserPermissions.purchaseOrder.view,
				label: 'View purchase orders',
				shortLabel: 'View',
				description: 'See the orders placed with suppliers and their progress.',
			},
			{
				key: DefinedUserPermissions.purchaseOrder.add,
				label: 'Create purchase orders',
				shortLabel: 'Create',
				description: 'Place a new order with a supplier.',
			},
			{
				key: DefinedUserPermissions.purchaseOrder.edit,
				label: 'Edit purchase orders',
				shortLabel: 'Edit',
				description: 'Change an order before it is received.',
			},
			{
				key: DefinedUserPermissions.purchaseOrder.delete,
				label: 'Delete purchase orders',
				shortLabel: 'Delete',
				description: 'Cancel and remove an order that was not received.',
			},
			{
				key: DefinedUserPermissions.purchaseOrder.receive,
				label: 'Receive goods',
				shortLabel: 'Receive',
				description:
					'Mark an order as delivered. This adds the goods to stock, so give it carefully.',
			},
			{
				key: DefinedUserPermissions.purchaseOrder.approve,
				label: 'Approve purchase orders',
				shortLabel: 'Approve',
				description: 'Sign off an order before it is sent to the supplier.',
			},
		],
	},
	{
		label: 'Supplier bills',
		permissions: [
			{
				key: DefinedUserPermissions.bill.view,
				label: 'View supplier bills',
				shortLabel: 'View',
				description: 'See what you owe suppliers and what has been paid.',
			},
			{
				key: DefinedUserPermissions.bill.add,
				label: 'Add supplier bills',
				shortLabel: 'Add',
				description: 'Record a bill received from a supplier.',
			},
			{
				key: DefinedUserPermissions.bill.edit,
				label: 'Edit supplier bills',
				shortLabel: 'Edit',
				description: 'Change a bill that has not been paid.',
			},
			{
				key: DefinedUserPermissions.bill.delete,
				label: 'Delete supplier bills',
				shortLabel: 'Delete',
				description: 'Remove a bill that was entered by mistake.',
			},
		],
	},
	{
		label: 'Sales',
		permissions: [
			{
				key: DefinedUserPermissions.sale.view,
				label: 'View sales',
				shortLabel: 'View',
				description: 'See your own sales and receipts.',
			},
			{
				key: DefinedUserPermissions.sale.viewAll,
				label: 'View sales of everyone',
				shortLabel: 'View all',
				description: 'See the sales made by every cashier, not only your own.',
			},
			{
				key: DefinedUserPermissions.sale.add,
				label: 'Record sales',
				shortLabel: 'Record',
				description: 'Use the POS to ring up and complete a sale.',
			},
			{
				key: DefinedUserPermissions.sale.void,
				label: 'Void sales',
				shortLabel: 'Void',
				description:
					'Cancel a completed sale and put its items back into stock.',
			},
			{
				key: DefinedUserPermissions.sale.refund,
				label: 'Refund sales',
				shortLabel: 'Refund',
				description:
					'Give money back for a sale. Different from a void, which cancels the sale itself.',
			},
			{
				key: DefinedUserPermissions.sale.discount,
				label: 'Give discounts',
				shortLabel: 'Discount',
				description: 'Take an amount off a sale at the POS.',
			},
			{
				key: DefinedUserPermissions.sale.priceOverride,
				label: 'Change a price at the POS',
				shortLabel: 'Override price',
				description:
					'Sell an item at a different price from the one on the product, for that sale only.',
			},
			{
				key: DefinedUserPermissions.sale.approveRemoval,
				label: 'Approve item removals',
				shortLabel: 'Approve removals',
				description:
					'Approve taking an item off a sale, when the business asks for a manager before an item is removed. Give it to managers, not cashiers.',
			},
			{
				key: DefinedUserPermissions.sale.reprint,
				label: 'Reprint receipts',
				shortLabel: 'Reprint',
				description:
					'Print another copy of a receipt that was already issued. A copy can be used to repeat a sale, so give it carefully.',
			},
		],
	},
	{
		label: 'Payments',
		permissions: [
			{
				key: DefinedUserPermissions.payment.view,
				label: 'View payments',
				shortLabel: 'View',
				description:
					'See the payments taken on sales, including unpaid and part-paid ones.',
			},
			{
				key: DefinedUserPermissions.payment.add,
				label: 'Take payments',
				shortLabel: 'Take',
				description:
					'Record money received, such as paying off an unpaid sale.',
			},
		],
	},
	{
		label: 'Invoices',
		permissions: [
			{
				key: DefinedUserPermissions.invoice.view,
				label: 'View invoices',
				shortLabel: 'View',
				description:
					'See the invoices of the business, what each one is for and whether it has been paid.',
			},
			{
				key: DefinedUserPermissions.invoice.add,
				label: 'Create invoices',
				shortLabel: 'Create',
				description:
					'Write a new invoice for a customer, as a draft or issued straight away.',
			},
			{
				key: DefinedUserPermissions.invoice.edit,
				label: 'Edit invoices',
				shortLabel: 'Edit',
				description:
					'Change a draft invoice, and issue it. An invoice that has been issued can no longer change.',
			},
			{
				key: DefinedUserPermissions.invoice.delete,
				label: 'Delete invoices',
				shortLabel: 'Delete',
				description:
					'Remove a draft invoice. An issued invoice is cancelled with a void instead.',
			},
			{
				key: DefinedUserPermissions.invoice.void,
				label: 'Void invoices',
				shortLabel: 'Void',
				description:
					'Cancel an issued invoice. The record stays, marked as void. One with payments must be refunded first.',
			},
			{
				key: DefinedUserPermissions.invoice.send,
				label: 'Send invoices',
				shortLabel: 'Send',
				description: 'Send an invoice to the customer by email or message.',
			},
			{
				key: DefinedUserPermissions.invoice.export,
				label: 'Export invoices',
				shortLabel: 'Export',
				description: 'Download invoices from the app.',
			},
		],
	},
	{
		label: 'Point of sale',
		permissions: [
			{
				key: DefinedUserPermissions.pos.open,
				label: 'Open the register',
				shortLabel: 'Open register',
				description: 'Start a POS session, usually with a cash float.',
			},
			{
				key: DefinedUserPermissions.pos.close,
				label: 'Close the register',
				shortLabel: 'Close register',
				description: 'End a POS session and count the cash.',
			},
			{
				key: DefinedUserPermissions.pos.cashMovement,
				label: 'Move cash in and out',
				shortLabel: 'Move cash',
				description:
					'Record cash put into or taken out of the drawer outside of sales.',
			},
		],
	},
	{
		label: 'POS terminals',
		permissions: [
			{
				key: DefinedUserPermissions.posTerminal.view,
				label: 'View POS terminals',
				shortLabel: 'View',
				description:
					'See the terminals registered to the business and their status.',
			},
			{
				key: DefinedUserPermissions.posTerminal.add,
				label: 'Add POS terminals',
				shortLabel: 'Add',
				description:
					'Register a new terminal and generate the code used to pair it.',
			},
			{
				key: DefinedUserPermissions.posTerminal.approve,
				label: 'Approve POS terminals',
				shortLabel: 'Approve',
				description:
					'Approve a terminal that has paired, so it can be used to sell.',
			},
			{
				key: DefinedUserPermissions.posTerminal.block,
				label: 'Block POS terminals',
				shortLabel: 'Block',
				description:
					'Stop a terminal from being used, without removing its pairing. It can be unblocked later.',
			},
			{
				key: DefinedUserPermissions.posTerminal.revoke,
				label: 'Revoke POS terminals',
				shortLabel: 'Revoke',
				description:
					'Remove a terminal pairing for good. It must be paired again from scratch to be used.',
			},
		],
	},
	{
		label: 'Reports',
		permissions: [
			{
				key: DefinedUserPermissions.report.view,
				label: 'View reports',
				shortLabel: 'View',
				description:
					'See sales and business figures on the dashboard and reports.',
			},
			{
				key: DefinedUserPermissions.report.export,
				label: 'Export reports',
				shortLabel: 'Export',
				description: 'Download report data from the app.',
			},
		],
	},
];
